// wishlistApi.js - API calls for wishlist functionality

import { API_BASE_URL, apiGet } from "./apiClient";

/**
 * Get the auth token from localStorage or cookies
 */
const getAuthToken = () => {
  return localStorage.getItem('token') || '';
};

/**
 * Get headers with auth (supports both JWT and Clerk)
 * @param {string} clerkUserId - Clerk user ID
 */
const getAuthHeaders = (clerkUserId = null) => {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  // Use Clerk user ID in header (verified on backend)
  if (clerkUserId) {
    headers['X-Clerk-User-Id'] = clerkUserId;
  } else {
    // Fallback to JWT token from localStorage
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  return headers;
};

/**
 * Fetch user's wishlist from the backend
 * @param {string} clerkUserId - Clerk user ID
 */
export const fetchWishlist = async (clerkUserId) => {
  try {
    // Deduplicated but deliberately NOT cached: several components (header
    // heart icons, wishlist page, useWishlistSync) request this on mount, and
    // dedup collapses those into one call — while skipping the cache keeps the
    // list correct immediately after an add/remove.
    const data = await apiGet('api/wishlist', {
      headers: getAuthHeaders(clerkUserId),
      credentials: 'include',
    });
    return data.wishlist || [];
  } catch (error) {
    throw new Error('❌ Error fetching wishlist:');
  }
};

/**
 * Add a trip to the wishlist
 * @param {Object} tripData - Trip data to add
 * @param {string} clerkUserId - Clerk user ID
 */
export const addToWishlistApi = async (tripData, clerkUserId) => {
  try {
    const response = await fetch(`${API_BASE_URL}api/wishlist`, {
      method: 'POST',
      headers: getAuthHeaders(clerkUserId),
      credentials: 'include',
      body: JSON.stringify({
        tripId: tripData.id,
        stateName: tripData.stateName,
        cityName: tripData.cityName,
        startDate: tripData.startDate,
        endDate: tripData.endDate,
        placesCount: tripData.placesCount || 0,
        activitiesCount: tripData.activitiesCount || 0,
        image: tripData.image,
        itineraryData: tripData.itineraryData,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to add to wishlist');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Error adding to wishlist:', error);
    throw error;
  }
};

/**
 * Remove a trip from the wishlist
 * @param {string} tripId - Trip ID to remove
 * @param {string} clerkUserId - Clerk user ID
 */
export const removeFromWishlistApi = async (tripId, clerkUserId) => {
  try {
    const response = await fetch(`${API_BASE_URL}api/wishlist/${encodeURIComponent(tripId)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(clerkUserId),
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to remove from wishlist');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Error removing from wishlist:', error);
    throw error;
  }
};

/**
 * Toggle wishlist status (add if not exists, remove if exists)
 * @param {Object} tripData - Trip data
 * @param {string} clerkUserId - Clerk user ID
 */
export const toggleWishlistApi = async (tripData, clerkUserId) => {
  try {
    const response = await fetch(`${API_BASE_URL}api/wishlist/toggle`, {
      method: 'POST',
      headers: getAuthHeaders(clerkUserId),
      credentials: 'include',
      body: JSON.stringify({
        tripId: tripData.id,
        stateName: tripData.stateName,
        startDate: tripData.startDate,
        endDate: tripData.endDate,
        placesCount: tripData.placesCount || 0,
        activitiesCount: tripData.activitiesCount || 0,
        image: tripData.image,
        itineraryData: tripData.itineraryData,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to toggle wishlist');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Error toggling wishlist:', error);
    throw error;
  }
};
